
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  BankAccount,
  Heading,
  NavBar,
  Row,
  StatusBar,
  CopyButton,
  BaseText,
  BaseModal,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';
import { moneyFormat } from '@/modules/common/helpers/currency';
import { getDateInFull } from '@/modules/common/helpers/date';

import {
  getDepositOperationType,
  getDepositMethod,
  getStatus
} from '@/modules/transaction/views/TransactionsLegacy/helpers/transaction';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { DepositsAPI } from '@/modules/transaction/services/deposits';
import { InvoicesAPI } from '@/modules/transaction/services/invoices';

import { Modal } from '@/modules/common/components/__deprecated__/Modal';
import { CloseButton } from '@/modules/common/components/buttons';
import { SimpleButton } from '@/modules/common/components/__deprecated__/SimpleButton';

import {
  DepositDetail,
  GetDepositDocument,
  GetDepositQuery
} from '@/modules/common/services/graphql/server/server.sdk';
import { ApolloQueryResult } from '@apollo/client';
import { getString } from '@/modules/common/helpers/resources';

interface IStep {
  title: string;
  description: string;
}

@Component({
  components: {
    BankAccount,
    LoaderIndicator,
    BaseModal,
    CloseButton,
    Heading,
    Modal,
    NavBar,
    SimpleButton,
    StatusBar,
    Row,
    CopyButton,
    BaseText
  },
  filters: {
    money: (value: number) => {
      return moneyFormat(value, false);
    },
    getDateInFull: (date: string) => {
      return getDateInFull(date);
    }
  }
})
export default class DepositModal extends Vue {
  @Prop({ type: String, required: true })
  private id!: string;

  @Prop({ type: Boolean, default: false })
  private isOpen!: boolean;

  private isLoading = true;
  private isSendingEmail = false;
  private isOpenModal = false;
  private isCancelingDeposit = false;
  private deposit?: DepositDetail;

  private getDateInFull = getDateInFull;
  private getDepositOperationType = getDepositOperationType;
  private getDepositMethod = getDepositMethod;
  private getTranscationStatus = getStatus;

  private NebraskaColors = NebraskaColors;

  private steps: IStep[] = [];

  private depositApi = new DepositsAPI();
  private invoiceApi = new InvoicesAPI();

  private async beforeMount() {
    await this.loadReceipt();
  }

  private isProcessing(deposit: DepositDetail | undefined) {
    return deposit?.status?.formatted === 'processing';
  }

  @Watch('id')
  private async loadReceipt() {
    try {
      if (this.id) {
        const { data }: ApolloQueryResult<GetDepositQuery> =
          await this.$apollo.query({
            query: GetDepositDocument,
            variables: {
              transactionId: this.id
            }
          });
        this.deposit = data.transaction?.deposit || ({} as any);
        const secondStep = await this.getStatusSteps();
        this.steps = [
          {
            title: getString(
              'portfolio_transaction_status_deposit_requested_step1_title'
            ),
            description: getDateInFull(this.deposit?.createdAt?.formatted || '')
          },
          {
            ...secondStep
          },
          {
            title: getString(
              'portfolio_transaction_status_deposit_requested_step3_title'
            ),
            description: ''
          }
        ];
        this.isLoading = false;
      }
    } catch (e) {
      this.deposit = undefined;
      this.isLoading = false;
    }
  }

  private getStatusSteps() {
    if (this.isProcessing(this.deposit) && !this.isByInvoice) {
      return {
        title: getString(
          'portfolio_transaction_status_deposit_processing_step2_title'
        ),
        description: getString(
          'portfolio_transaction_status_deposit_processing_step2_description'
        )
      };
    } else if (!this.isProcessing(this.deposit) && !this.isByInvoice) {
      return {
        title: getString(
          'portfolio_transaction_status_deposit_requested_step2_title'
        ),
        description: getString(
          'portfolio_transaction_status_deposit_requested_step2_description'
        )
      };
    } else if (!this.isProcessing(this.deposit) && this.isByInvoice) {
      return {
        title: getString(
          'portfolio_transaction_status_invoice_requested_step2_title'
        ),
        description: getString(
          'portfolio_transaction_status_invoice_requested_step2_description'
        )
      };
    } else {
      return {
        title: getString(
          'portfolio_transaction_status_invoice_processing_step2_title'
        ),
        description: getString(
          'portfolio_transaction_status_invoice_processing_step2_description'
        )
      };
    }
  }

  private getCurrentStep(deposit: DepositDetail) {
    if (!deposit?.status?.value) return 0;

    switch (this.getTranscationStatus(deposit.status.value)) {
      case 'Pendente':
        return 1;
      case 'Processando':
        return 2;
      case 'Concluído':
        return 3;
      default:
        return 0;
    }
  }

  private get isByInvoice() {
    return this.deposit?.isDepositByInvoice;
  }

  private async onClickCancelDeposit() {
    this.isOpenModal = false;
    this.isCancelingDeposit = true;
    try {
      if (this.isByInvoice) {
        await this.invoiceApi.cancelInvoice(this.id);
      } else {
        await this.depositApi.cancelDeposit(this.id);
      }

      this.isCancelingDeposit = false;
      this.$emit('close');
      this.$emit('cancel-transaction');
    } catch (e) {
      this.isOpenModal = false;
      this.isCancelingDeposit = false;
    }
  }

  private async onClickSendEmail() {
    this.isSendingEmail = true;
    try {
      await this.depositApi.sendDepositByEmail(this.id);

      this.isSendingEmail = false;
    } catch (e) {
      this.isSendingEmail = false;
    }
  }

  private onClickViewAsPDF() {
    const newTab = window.open(this.deposit?.docToPDF?.toString(), '_blank');
    newTab?.focus();
  }

  private openModal() {
    this.isOpenModal = true;
  }

  private closeModal() {
    this.isOpenModal = false;
  }

  @Emit('copy')
  private onCopy(value: string) {
    return value;
  }

  private get buttonCancelLabel() {
    return this.isByInvoice ? 'BOLETO' : 'DEPÓSITO';
  }

  private get transactionInvest() {
    return this.deposit?.transactionInvest;
  }

  private get status() {
    return this.deposit?.status?.formatted;
  }

  private get creationDate() {
    return this.deposit?.createdAt?.formatted;
  }

  private get operationDate() {
    return this.deposit?.operatedAt?.formatted;
  }

  private get totalAmount() {
    if (!this.deposit?.transactionInvest) return 0;

    return this.deposit?.transactionInvest.length > 1
      ? this.deposit?.amount?.formatted
      : 0;
  }
}
